import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { Icon, type IconName } from '../ui/Icon';
import { useModalStore } from '../../stores/modal.store';
import { useUIStore } from '../../stores/ui.store';
import { useAuthStore } from '../../stores/auth.store';
import { useScrollLock } from '../../lib/overlay';
import { fmtEur } from '../../lib/utils';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { NEW_ACTIONS, visibleNewActions } from '../../lib/newActions';
import { frozenModuleForPath } from '../../lib/frozen';
import { useActionStream } from '../../features/oggi/useActionStream';
import { useIsMobile } from '../../hooks/useIsMobile';
import { toast } from '../../stores/toast.store';

/**
 * Command palette in stile Raycast/Linear.
 *
 * Layout:
 *  - Glass blur backdrop scuro
 *  - Card centrale top-aligned, scale-in animation
 *  - Search input grande con icona brand
 *  - Quick actions quando query vuota (Da fare adesso + Recent + Suggested)
 *  - Risultati raggruppati per categoria con icona colorata
 *  - Highlight dei match nella label
 *  - Footer con keyboard shortcut
 */

interface SearchResults {
  clients: Array<{ id: string; legalName: string; shortName: string; status: string; sector: string | null }>;
  projects: Array<{ id: string; code: string; name: string; division: string; status: string; client: { shortName: string } }>;
  invoices: Array<{ id: string; number: string | null; status: string; total: string; client: { shortName: string } }>;
  candidates: Array<{ id: string; firstName: string; lastName: string; skill: string | null; city: string | null; stage: string }>;
  courses: Array<{ id: string; name: string; type: string; status: string }>;
  suppliers: Array<{ id: string; fullName: string; city: string | null; active: boolean }>;
  total: number;
}

type ActionItem =
  | { kind: 'navigate'; to: string }
  | { kind: 'modal'; modal: string; payload?: Record<string, unknown> }
  | { kind: 'fn'; run: () => void };

interface FlatItem {
  id: string;
  label: string;
  sub?: string;
  badge?: string;
  group: string;
  groupIcon: IconName;
  groupColor: string;            // var(--xxx) per il cerchio icona
  action: ActionItem;
}

// ──────────────────────────────────────────────────────────────
// Quick actions sempre disponibili (filtrabili anche con query)
//
// FASE 5: le creazioni ("Nuovo cliente", "Nuovo progetto", …) NON sono più
// hard-coded qui — vengono derivate dalla fonte unica src/lib/newActions.ts e
// gated sui permessi dell'utente (vedi createActions nel componente). Qui
// restano solo le voci di navigazione.
// ──────────────────────────────────────────────────────────────
const QUICK_ACTIONS: FlatItem[] = [
  { id: 'go-dashboard',   label: 'Vai a Dashboard',   sub: 'Cockpit dei KPI',                    group: 'Naviga',        groupIcon: 'dashboard', groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/' } },
  { id: 'go-tasks',       label: 'Vai a Task',        sub: 'Cosa c\'è da fare',                  group: 'Naviga',        groupIcon: 'tasks',     groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/tasks' } },
  { id: 'go-deals',       label: 'Vai a Trattative',  sub: 'Pipeline voucher, rinnovi, estensioni', group: 'Naviga',      groupIcon: 'handshake', groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/trattative' } },
  { id: 'go-crm',         label: 'Vai a CRM',         sub: 'Clienti e contatti',                 group: 'Naviga',        groupIcon: 'users',     groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/crm' } },
  // I soldi hanno una voce sola: la Contabilità vive nella tab Documenti di Amministrazione
  { id: 'go-admin',       label: 'Vai a Amministrazione', sub: 'Cassa, incassi, uscite, fisco',  group: 'Naviga',        groupIcon: 'landmark',  groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/admin' } },
  { id: 'go-documenti',   label: 'Vai a Documenti',   sub: 'Fatture e preventivi',               group: 'Naviga',        groupIcon: 'invoice',   groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/admin?tab=documenti' } },
  { id: 'go-services',    label: 'Vai a Abbonamenti', sub: 'Stripe, hosting, canoni',           group: 'Naviga',        groupIcon: 'link',      groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/services' } },
  { id: 'go-design',      label: 'Vai a Digital Design', sub: 'Progetti di design',              group: 'Naviga',        groupIcon: 'palette',   groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/divisions/design' } },
  { id: 'go-talent',      label: 'Vai a Talent',      sub: 'Academy e Body Rental',              group: 'Naviga',        groupIcon: 'talent',    groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/talent' } },
  { id: 'go-report',      label: 'Vai a Report',      sub: 'Andamento e analisi',                group: 'Naviga',        groupIcon: 'chart',     groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/report' } },
  { id: 'go-vault',       label: 'Vai al Vault',      sub: 'Credenziali condivise',              group: 'Naviga',        groupIcon: 'lock',      groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/vault' } },
  { id: 'go-settings',    label: 'Vai a Impostazioni', sub: 'Ruoli, integrazioni, preferenze',   group: 'Naviga',        groupIcon: 'settings',  groupColor: 'var(--accent)', action: { kind: 'navigate', to: '/settings' } },
];
// FASE 0: niente "Vai a…" verso i moduli in pausa (lib/frozen.ts). La lista
// sopra resta completa: si riattiva da sola togliendo il modulo dalla costante.
const NAV_ACTIONS: FlatItem[] = QUICK_ACTIONS.filter(
  (a) => a.action.kind !== 'navigate' || !frozenModuleForPath(a.action.to),
);

const RECENT_KEY = 'mn:cmdk:recent:v1';
const RECENT_MAX = 5;

function loadRecent(): FlatItem[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveRecent(items: FlatItem[]) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, RECENT_MAX))); } catch {}
}

// ──────────────────────────────────────────────────────────────
// Highlight match — wrappa le porzioni della label che matchano la query
// ──────────────────────────────────────────────────────────────
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const q = query.trim().toLowerCase();
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'transparent', color: 'var(--accent)', fontWeight: 800 }}>
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Il corpo monta solo da aperta: useActionStream attiva le query dello
  // stream "Oggi" (fatture, preventivi, overview) che non devono partire su
  // ogni pagina solo perché la palette vive nella Topbar. Da chiusa il
  // comportamento è identico a prima (render null, nessun listener).
  if (!open) return null;
  return <CommandPaletteBody open onClose={onClose} />;
}

function CommandPaletteBody({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const openModal = useModalStore((s) => s.open);
  const canvas = useUIStore((s) => s.canvas);
  const setCanvas = useUIStore((s) => s.setCanvas);
  const user = useAuthStore((s) => s.user);
  const mobile = useIsMobile();
  const [q, setQ] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [recent, setRecent] = useState<FlatItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Blocca lo scroll del body mentre la palette è aperta
  useScrollLock(open);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<SearchResults>(`/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });

  // Reset all'apertura + load recent
  useEffect(() => {
    if (open) {
      setQ('');
      setSelectedIdx(0);
      setRecent(loadRecent());
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Esegue un'azione (navigate / modal / fn) e salva in recent
  const runItem = (item: FlatItem) => {
    // Salva nei recent solo le entità (no quick action / comandi / contestuali / pinnati)
    if (!/^(qa-|go-|sys-|ctx-|pin-)/.test(item.id)) {
      const next = [item, ...recent.filter((r) => r.id !== item.id)].slice(0, RECENT_MAX);
      setRecent(next);
      saveRecent(next);
    }
    if (item.action.kind === 'navigate') {
      navigate(item.action.to);
    } else if (item.action.kind === 'modal') {
      openModal(item.action.modal as any, item.action.payload);
    } else {
      item.action.run();
    }
    onClose();
  };

  // ── Layer 0: comandi di sistema + azioni contestuali alla route corrente ──
  const dynamicCommands: FlatItem[] = useMemo(() => {
    const cmds: FlatItem[] = [
      {
        id: 'sys-theme', label: canvas === 'dark' ? 'Tema chiaro' : 'Tema scuro',
        sub: 'Cambia aspetto dell\'interfaccia', group: 'Comandi',
        groupIcon: canvas === 'dark' ? 'sun' : 'moon', groupColor: 'var(--accent)',
        action: { kind: 'fn', run: () => setCanvas(canvas === 'dark' ? 'light' : 'dark') },
      },
    ];
    // Azioni contestuali: dipendono da dove ti trovi adesso.
    // Fase 0: su una pagina in pausa non ha senso proporre "in questa pagina".
    if (frozenModuleForPath(location.pathname)) return cmds;
    const m = location.pathname.match(/^\/projects\/([^/]+)/);
    if (m) {
      cmds.push({
        id: 'ctx-task-here', label: 'Nuovo task in questo progetto',
        sub: 'Aggiungi un\'attività qui', group: 'In questa pagina',
        groupIcon: 'plus', groupColor: 'var(--accent)',
        action: { kind: 'modal', modal: 'newTask', payload: { projectId: m[1] } },
      });
    }
    const c = location.pathname.match(/^\/clients\/([^/]+)/);
    if (c && !mobile) {
      cmds.push({
        id: 'ctx-project-here', label: 'Nuovo progetto per questo cliente',
        sub: 'Parte già col cliente compilato', group: 'In questa pagina',
        groupIcon: 'plus', groupColor: 'var(--accent)',
        action: { kind: 'modal', modal: 'newProject', payload: { clientId: c[1] } },
      });
      cmds.push({
        id: 'ctx-quote-here', label: 'Nuovo preventivo per questo cliente',
        sub: 'Proposta firmabile', group: 'In questa pagina',
        groupIcon: 'file', groupColor: 'var(--accent)',
        action: { kind: 'modal', modal: 'newQuote', payload: { clientId: c[1] } },
      });
    }
    return cmds;
  }, [canvas, location.pathname, mobile, setCanvas]);

  // ── Azioni rapide di creazione: stessa fonte del NewMenu (FASE 5), ma gated
  //    sui permessi dell'utente. Prima la palette le mostrava SEMPRE, anche a
  //    chi non poteva scrivere: ora rispetta i permessi (più sicura). ──
  const createActions: FlatItem[] = useMemo(() => {
    const canWrite = (module: ModuleKey) => {
      if (!user) return false;
      const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
      return hasAtLeast(lvl, PermissionLevel.WRITE);
    };
    const actions = visibleNewActions(NEW_ACTIONS, canWrite)
      .filter((a) => !mobile || a.key === 'new-task')
      .map<FlatItem>((a) => ({
      id: `qa-${a.key}`,
      label: a.paletteLabel,
      sub: a.paletteSub,
      group: 'Azioni rapide',
      groupIcon: 'plus',
      groupColor: a.paletteColor,
      action: { kind: 'modal', modal: a.modal },
    }));
    if (mobile) {
      actions.push({
        id: 'sys-desktop-creations',
        label: 'Altre creazioni',
        sub: 'Clienti, progetti, documenti e configurazioni si fanno dal computer',
        group: 'Dal computer',
        groupIcon: 'help',
        groupColor: 'var(--mute)',
        action: {
          kind: 'fn',
          run: () => toast.info(
            'Dal computer',
            'Clienti, progetti, documenti e configurazioni richiedono il computer.',
          ),
        },
      });
    }
    return actions;
  }, [mobile, user]);

  // ── Sezione pinnata "Da fare adesso": top 3 item urgenti (band 0/1) dallo
  //    stream "Oggi". Visibile solo a query vuota, sparisce appena si digita.
  //    Le mutation (incassa/sollecita/…) vivono dentro ActionItemRow e non
  //    sono esportate: qui Invio naviga all'entità, quindi l'azione è
  //    etichettata 'Apri' — niente logica di mutation duplicata. ──
  const { items: streamItems } = useActionStream();
  const pinnedItems: FlatItem[] = useMemo(
    () => streamItems
      .filter((it) => it.band <= 1) // solo Critico (0) e Oggi (1)
      .slice(0, 3)                  // già ordinati per banda + severità dal composer
      .map<FlatItem>((it) => ({
        id: `pin-${it.key}`,
        label: it.title,
        sub: it.amount != null ? `${it.sub} · € ${fmtEur(it.amount)}` : it.sub,
        badge: 'Apri',
        group: 'Da fare adesso',
        groupIcon: it.icon,
        // Rosso semantico solo per i critici, come il tile di ActionItemRow
        groupColor: it.band === 0 ? 'var(--alert)' : 'var(--accent)',
        action: { kind: 'navigate', to: it.entity.path },
      })),
    [streamItems],
  );

  const allActions = useMemo(
    () => [...dynamicCommands, ...createActions, ...NAV_ACTIONS],
    [dynamicCommands, createActions],
  );

  // Risultati ricerca server appiattiti
  const serverResults: FlatItem[] = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.clients ?? []).map<FlatItem>((c) => ({
        id: `client-${c.id}`,
        label: c.legalName,
        sub: c.sector ?? '',
        badge: c.status,
        group: 'Clienti',
        groupIcon: 'users',
        groupColor: 'var(--accent)',
        action: { kind: 'navigate', to: `/clients/${c.id}` },
      })),
      ...(data.projects ?? []).map<FlatItem>((p) => ({
        id: `project-${p.id}`,
        label: `${p.code} — ${p.name}`,
        sub: p.client.shortName,
        badge: p.status,
        group: 'Progetti',
        groupIcon: 'cube',
        groupColor: 'var(--accent)',
        action: { kind: 'navigate', to: `/projects/${p.id}` },
      })),
      ...(data.invoices ?? []).map<FlatItem>((i) => ({
        id: `invoice-${i.id}`,
        label: i.number ?? 'BOZZA',
        sub: `${i.client.shortName} · € ${Number(i.total).toLocaleString('it-IT')}`,
        badge: i.status,
        group: 'Fatture',
        groupIcon: 'invoice',
        groupColor: 'var(--accent)',
        action: { kind: 'navigate', to: `/invoices/${i.id}` },
      })),
      ...(data.candidates ?? []).map<FlatItem>((c) => ({
        id: `candidate-${c.id}`,
        label: `${c.firstName} ${c.lastName}`,
        sub: [c.skill, c.city].filter(Boolean).join(' · '),
        badge: c.stage.replace(/_/g, ' ').toLowerCase(),
        group: 'Candidati',
        groupIcon: 'talent',
        groupColor: 'var(--accent)',
        action: { kind: 'navigate', to: `/candidates/${c.id}` },
      })),
      ...(data.courses ?? []).map<FlatItem>((c) => ({
        id: `course-${c.id}`,
        label: c.name,
        sub: c.type,
        badge: c.status,
        group: 'Corsi',
        groupIcon: 'brief',
        groupColor: 'var(--accent)',
        action: { kind: 'navigate', to: `/courses/${c.id}` },
      })),
      ...(data.suppliers ?? []).map<FlatItem>((s) => ({
        id: `supplier-${s.id}`,
        label: s.fullName,
        sub: s.city ?? '',
        badge: s.active ? 'attivo' : 'inattivo',
        group: 'Professionisti',
        groupIcon: 'brief',
        groupColor: 'var(--accent)',
        action: { kind: 'navigate', to: `/suppliers/${s.id}` },
      })),
    ];
  }, [data]);

  // Azioni (comandi dinamici + quick actions) filtrate dalla query
  const filteredActions = useMemo(() => {
    if (!q.trim()) return allActions;
    const needle = q.toLowerCase();
    return allActions.filter((a) =>
      a.label.toLowerCase().includes(needle) || (a.sub ?? '').toLowerCase().includes(needle),
    );
  }, [q, allActions]);

  // Lista flat finale (per navigazione tastiera): se query vuota = pinnati + recent + quick actions
  // se query >= 2 chars = server results + quick actions filtrate
  const flat: FlatItem[] = useMemo(() => {
    if (!q.trim()) {
      return [...pinnedItems, ...recent, ...allActions];
    }
    if (q.trim().length < 2) return filteredActions;
    return [...serverResults, ...filteredActions];
  }, [q, pinnedItems, recent, allActions, filteredActions, serverResults]);

  // Group by category mantenendo l'ordine
  const grouped = useMemo(() => {
    const keys: string[] = [];
    const items: Record<string, FlatItem[]> = {};
    // Sezione pinnata "Da fare adesso" SEMPRE per prima quando query vuota
    if (!q.trim() && pinnedItems.length) {
      keys.push('Da fare adesso');
      items['Da fare adesso'] = pinnedItems;
    }
    // Sezione Recent subito dopo quando query vuota
    if (!q.trim() && recent.length) {
      keys.push('Recenti');
      items['Recenti'] = recent;
    }
    for (const item of (q.trim() ? flat : allActions)) {
      if (!items[item.group]) {
        items[item.group] = [];
        keys.push(item.group);
      }
      items[item.group]!.push(item);
    }
    return { keys, items };
  }, [q, pinnedItems, recent, flat, allActions]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, flat.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const target = flat[selectedIdx];
        if (target) runItem(target);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, selectedIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selection quando cambia la lista (i pinnati possono arrivare async)
  useEffect(() => { setSelectedIdx(0); }, [q, recent.length, pinnedItems.length]);

  if (!open) return null;

  return (
    <div
      className="cmdk-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Cerca in Masamune OS"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(8, 8, 14, 0.45)',
        backdropFilter: 'blur(8px) saturate(140%)',
        WebkitBackdropFilter: 'blur(8px) saturate(140%)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '14vh', paddingLeft: 16, paddingRight: 16,
        animation: 'cmdk-fade-in 0.18s ease-out',
      }}
    >
      <style>{`
        @keyframes cmdk-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cmdk-pop-in  { from { opacity: 0; transform: translateY(-8px) scale(0.98); } to { opacity: 1; transform: none; } }
        @keyframes cmdk-spin    { to { transform: rotate(360deg); } }
        .cmdk-spinner { animation: cmdk-spin 0.8s linear infinite; }
        .cmdk-row { transition: background 0.12s ease; }
        .cmdk-row:hover { background: var(--paper-2); }
        .cmdk-row[aria-selected="true"] {
          background: var(--accent-surface-strong);
          box-shadow: inset 2px 0 0 var(--accent);
        }
        /* In dark --accent-surface-strong resta chiaro (calcolato su :root): stessa ricetta delle tab dark */
        body[data-canvas="dark"] .cmdk-row[aria-selected="true"] {
          background: color-mix(in oklab, var(--accent) 14%, var(--surface));
        }
        .cmdk-row[aria-selected="true"] .cmdk-arrow {
          opacity: 1; transform: translateX(0);
        }
        .cmdk-arrow {
          opacity: 0; transform: translateX(-4px); transition: opacity 0.15s ease, transform 0.15s ease;
        }
      `}</style>

      <div
        className="cmdk-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 680,
          background: 'var(--paper)',
          borderRadius: 'var(--r-card)',
          border: '1px solid var(--line)',
          boxShadow: 'var(--elev-pop)',
          overflow: 'hidden',
          animation: 'cmdk-pop-in 0.18s ease-out',
        }}
      >
        {/* Search input header */}
        <div className="cmdk-search flex items-center gap-3 px-4 py-3.5 border-b border-line">
          <div
            className="grid place-items-center rounded-full shrink-0"
            style={{ width: 30, height: 30, background: 'var(--ink)', color: 'var(--paper)' }}
          >
            <Icon name="search" size={14} />
          </div>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca clienti, progetti, fatture… o digita un'azione (es. 'nuovo cliente')"
            className="flex-1 outline-none text-[16px] bg-transparent placeholder:text-mute-2"
            style={{ fontWeight: 500, letterSpacing: '-0.005em' }}
            autoComplete="off"
            spellCheck={false}
          />
          {isFetching && (
            <div
              className="cmdk-spinner"
              style={{
                width: 14, height: 14, borderRadius: '50%',
                border: '2px solid var(--line)',
                borderTopColor: 'var(--ink)',
              }}
            />
          )}
          <button type="button" className="cmdk-close m-only btn btn-ghost btn-icon" onClick={onClose} aria-label="Chiudi ricerca">
            <Icon name="x" size={14} />
          </button>
          <span className="t-kbd">ESC</span>
        </div>

        {/* Results */}
        <div className="cmdk-results overflow-y-auto" style={{ maxHeight: 'min(520px, 60vh)' }}>
          {/* Empty: nessuna query, niente urgenze pinnate né recent */}
          {!q.trim() && pinnedItems.length === 0 && recent.length === 0 && (
            <div className="px-6 py-8 text-center">
              <div
                className="mx-auto mb-3 grid place-items-center"
                style={{
                  width: 48, height: 48, borderRadius: 'var(--r-tile)',
                  background: 'var(--ink)',
                  color: 'white',
                }}
              >
                <Icon name="search" size={20} />
              </div>
              <div className=" font-semibold mb-1">Cerca ovunque in Masamune OS</div>
              <div className="mute text-[13px]">
                Inizia a digitare per cercare clienti, progetti, fatture, candidati…
                <br />
                oppure scegli un'azione qui sotto
              </div>
            </div>
          )}

          {/* Query non vuota ma <2 char */}
          {q.trim() && q.trim().length < 2 && (
            <div className="px-4 py-6 text-center mute text-[13px]">
              Almeno 2 caratteri per cercare nei dati…
            </div>
          )}

          {/* Loading */}
          {q.trim().length >= 2 && isLoading && (
            <div className="px-4 py-8 text-center mute text-[13px]">Ricerca in corso…</div>
          )}

          {/* No results */}
          {q.trim().length >= 2 && !isLoading && serverResults.length === 0 && filteredActions.length === 0 && (
            <div className="px-4 py-10 text-center mute text-[13px]">
              Nessun risultato per "<strong>{q}</strong>"
            </div>
          )}

          {/* Sezioni */}
          {grouped.keys.map((groupName) => (
            <div key={groupName} className="py-1">
              <div className="px-4 pt-2.5 pb-1 flex items-center gap-2">
                <span className="t-micro">{groupName}</span>
                <span className="mute text-[12px] mono">{grouped.items[groupName]!.length}</span>
                <div className="flex-1 h-px" style={{ background: 'var(--line)', opacity: 0.5 }} />
              </div>
              {grouped.items[groupName]!.map((item) => {
                const idx = flat.findIndex((f) => f.id === item.id);
                const active = idx === selectedIdx;
                return (
                  <div
                    key={item.id}
                    className="cmdk-row px-3 py-2 cursor-pointer flex items-center gap-3 mx-1.5 rounded-1"
                    aria-selected={active}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    onClick={() => runItem(item)}
                  >
                    <div
                      className="grid place-items-center shrink-0 rounded-1"
                      style={{
                        width: 30, height: 30,
                        background: `color-mix(in oklab, ${item.groupColor} 14%, transparent)`,
                        color: item.groupColor,
                      }}
                    >
                      <Icon name={item.groupIcon} size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate leading-tight">
                        {highlightMatch(item.label, q)}
                      </div>
                      {item.sub && (
                        <div className="mute text-[12px] truncate mt-0.5">{item.sub}</div>
                      )}
                    </div>
                    {item.badge && (
                      <span
                        className="text-[12px] uppercase tracking-wider mono shrink-0 px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--paper-2)', color: 'var(--mute)' }}
                      >
                        {item.badge}
                      </span>
                    )}
                    <Icon name="arrowRight" size={12} className="mute cmdk-arrow" />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="cmdk-footer px-4 py-2 border-t border-line text-[12px] mute flex items-center gap-4">
          <span className="flex items-center gap-1"><span className="t-kbd">↑</span> <span className="t-kbd">↓</span> naviga</span>
          <span className="flex items-center gap-1"><span className="t-kbd">↵</span> apri</span>
          <span className="flex items-center gap-1"><span className="t-kbd">ESC</span> chiudi</span>
          <span className="spacer" />
          {q.trim().length >= 2 && (
            <span className="mono">
              {serverResults.length + filteredActions.length} risultati
              {data?.total !== undefined && ` su ${data.total}`}
            </span>
          )}
          {!q.trim() && recent.length > 0 && (
            <span className="mono">{recent.length} recenti</span>
          )}
        </div>
      </div>
    </div>
  );
}
